import React, { useEffect, useState } from 'react';
import { logError } from '@/lib/error-handler';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Star, TrendingUp, Users, MessageSquare, Clock, RefreshCw } from 'lucide-react';
import { contactScoringService, ContactScore } from '@/services/analytics/contactScoringService';
import { cn } from '@/lib/utils';

interface ContactScoringCardProps {
  contactId: string;
  compact?: boolean;
}

export function ContactScoringCard({ contactId, compact = false }: ContactScoringCardProps) {
  const [contactScore, setContactScore] = useState<ContactScore | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadContactScore();
  }, [contactId]);

  const loadContactScore = async () => {
    try {
      setLoading(true);
      let score = await contactScoringService.getContactScore(contactId);

      // If no score exists or it's older than 7 days, calculate a new one
      if (!score || isScoreStale(score.last_calculated)) {
        score = await contactScoringService.calculateContactScore(contactId);
      }

      setContactScore(score);
    } catch (error) {
      logError('Error loading contact score:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      const score = await contactScoringService.calculateContactScore(contactId);
      setContactScore(score);
    } catch (error) {
      logError('Error refreshing contact score:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const isScoreStale = (lastCalculated: string): boolean => {
    const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    return new Date(lastCalculated) < weekAgo;
  };

  const getScoreColor = (level: string) => {
    switch (level) {
      case 'high':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'medium':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getScoreIcon = (score: number) => {
    if (score >= 80) return '🌟';
    if (score >= 60) return '⭐';
    if (score >= 40) return '✨';
    return '💫';
  };

  if (loading) {
    return (
      <Card className={cn(compact && 'p-3')}>
        <CardHeader className={cn('pb-2', compact && 'p-0 pb-2')}>
          <CardTitle className={cn('flex items-center gap-2', compact && 'text-sm')}>
            <Star className="w-4 h-4" />
            Contact Score
          </CardTitle>
        </CardHeader>
        <CardContent className={cn(compact && 'p-0 pt-2')}>
          <div className="animate-pulse space-y-2">
            <div className="h-6 bg-gray-200 rounded w-1/2"></div>
            <div className="h-2 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!contactScore) {
    return (
      <Card className={cn(compact && 'p-3')}>
        <CardHeader className={cn('pb-2', compact && 'p-0 pb-2')}>
          <CardTitle className={cn('flex items-center gap-2', compact && 'text-sm')}>
            <Star className="w-4 h-4" />
            Contact Score
          </CardTitle>
        </CardHeader>
        <CardContent className={cn(compact && 'p-0 pt-2')}>
          <p className="text-sm text-muted-foreground mb-2">No scoring data available.</p>
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Calculate Score'}
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (compact) {
    return (
      <div className="flex items-center gap-3 p-3 border rounded-lg">
        <div className="flex items-center gap-2">
          <span className="text-lg">{getScoreIcon(contactScore.overall_score)}</span>
          <span className="font-medium">{contactScore.overall_score}/100</span>
        </div>
        <Badge className={getScoreColor(contactScore.score_level)}>
          {contactScore.score_level} priority
        </Badge>
        <Button variant="ghost" size="sm" onClick={handleRefresh} disabled={refreshing}>
          <RefreshCw className={cn('w-3 h-3', refreshing && 'animate-spin')} />
        </Button>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Star className="w-5 h-5" />
            Contact Score
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={cn('w-4 h-4', refreshing && 'animate-spin')} />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Overall Score */}
        <div className="text-center">
          <div className="text-4xl mb-2">{getScoreIcon(contactScore.overall_score)}</div>
          <div className="text-2xl font-bold mb-2">{contactScore.overall_score}/100</div>
          <Badge className={cn('mb-2', getScoreColor(contactScore.score_level))}>
            {contactScore.score_level} priority contact
          </Badge>
        </div>

        {/* Score Breakdown */}
        <div className="space-y-3">
          <h4 className="font-medium text-sm">Score Breakdown</h4>

          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Users className="w-4 h-4 text-blue-600" />
              <div className="flex-1">
                <div className="flex justify-between text-sm mb-1">
                  <span>Influence</span>
                  <span className="font-medium">{contactScore.influence_score}%</span>
                </div>
                <Progress value={contactScore.influence_score} className="h-2" />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <TrendingUp className="w-4 h-4 text-green-600" />
              <div className="flex-1">
                <div className="flex justify-between text-sm mb-1">
                  <span>Engagement</span>
                  <span className="font-medium">{contactScore.engagement_score}%</span>
                </div>
                <Progress value={contactScore.engagement_score} className="h-2" />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Clock className="w-4 h-4 text-purple-600" />
              <div className="flex-1">
                <div className="flex justify-between text-sm mb-1">
                  <span>Responsiveness</span>
                  <span className="font-medium">{contactScore.responsiveness_score}%</span>
                </div>
                <Progress value={contactScore.responsiveness_score} className="h-2" />
              </div>
            </div>
          </div>
        </div>

        {/* Score Factors */}
        <div className="pt-3 border-t">
          <h4 className="font-medium text-sm mb-2">Contributing Factors</h4>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Position:</span>
              <span>{contactScore.factors.position_weight}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Activity:</span>
              <span>{contactScore.factors.activity_frequency}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Response Time:</span>
              <span>{contactScore.factors.response_time}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Email Engagement:</span>
              <span>{contactScore.factors.email_engagement}%</span>
            </div>
          </div>
        </div>

        {/* Last Updated */}
        <div className="text-xs text-muted-foreground text-center pt-2 border-t">
          Last updated: {new Date(contactScore.last_calculated).toLocaleDateString()}
          {isScoreStale(contactScore.last_calculated) && (
            <span className="text-yellow-600 ml-1">(Update recommended)</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
